'use client';

import { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { WithContext, BreadcrumbList } from 'schema-dts';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import { generateBreadcrumbSchema } from '@/lib/schemas/breadcrumbSchema';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  image: string;
  alt: string;
  category: string;
  date: string;
  author: string;
  readTime: string;
}

const blogPosts: BlogPost[] = [
{
  id: 'dmat-germany-masters-indian-students-2026',
  title: 'The Digital Master Test (dMAT): A Complete Guide for Indian Master\'s Applicants (2026/27)',
  excerpt: 'Starting mid-2026, a new standardized academic aptitude test called the Digital Master Test (dMAT) is mandatory for selected Indian Master\'s applicants to Germany. Learn who is affected, key dates, and how it integrates with the APS process.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_11e0ff3b1-1782899776886.png",
  alt: 'German university campus with students preparing for the Digital Master Test dMAT',
  category: 'Germany',
  date: '2026-07-01',
  author: 'Ashok Variyani',
  readTime: '10 min read'
},
{
  id: 'studienkolleg-germany-indian-students-2026',
  title: 'Studienkolleg in Germany: The Complete Guide for Indian Students',
  excerpt: 'Everything an Indian student needs to know about Studienkolleg — the mandatory one-year preparatory programme that bridges Class 12 to a German university. APS, courses, costs, timeline, and FSP explained.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_193f7c02b-1780295165428.png",
  alt: 'Brandenburg Gate in Berlin representing German university education for Indian students',
  category: 'Germany',
  date: '2026-05-13',
  author: 'Ashok Variyani',
  readTime: '15 min read'
},
{
  id: 'study-mbbs-abroad-india-2026',
  title: 'Your Ultimate Guide to Studying MBBS Abroad (2026–27): Everything an Indian Student Needs to Know',
  excerpt: 'Dreaming of becoming a doctor but worried about NEET ranks and sky-high private college fees? Discover how studying MBBS abroad can be your smartest, most affordable path to a medical career — with full NMC recognition.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_132507f3e-1778690624850.png",
  alt: 'Medical students in white coats studying together in a modern university campus abroad',
  category: 'MBBS Abroad',
  date: '2026-06-01',
  author: 'Ashok Variyani',
  readTime: '14 min read'
},
{

  id: 'china',
  title: 'Study in China: Complete Guide for International Students',
  excerpt: 'China has rapidly emerged as one of the world\'s leading study destinations, offering international students access to cutting-edge research facilities, affordable education, and immersive cultural experiences.',
  image: "/assets/images/china_a-1775728175921.jpg",
  alt: 'Great Wall of China with students exploring ancient architecture',
  category: 'China',
  date: '2026-01-19',
  author: 'Ashok Variyani',
  readTime: '12 min read'
},
{
  id: 'germany-mba-career-2026',
  title: 'Unlocking Your Career in Germany: A Guide to MBA Job Opportunities in 2026',
  excerpt: 'Discover how pursuing an MBA in Germany can transform your career with exceptional ROI, 90% employment rates, and pathways to permanent residency in Europe\'s strongest economy.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_16b3cd0d7-1765302149471.png",
  alt: 'Modern business district in Frankfurt Germany with professionals and skyscrapers',
  category: 'Germany',
  date: '2026-01-23',
  author: 'Ashok Variyani',
  readTime: '18 min read'
},
{
  id: 'uae-scholarships-2026',
  title: 'The Ultimate Guide to Fully Funded Scholarships in the UAE: Your Gateway to a World-Class Education in 2026',
  excerpt: 'Discover how to secure fully funded scholarships in the UAE covering tuition, stipends up to AED 40,000, and exclusive career opportunities in one of the world\'s most innovative education hubs.',
  image: "https://images.unsplash.com/photo-1731755512484-8bf853f36abc",
  alt: 'Dubai skyline with Burj Khalifa and modern architecture showcasing UAE education hub',
  category: 'UAE',
  date: '2026-01-22',
  author: 'Ashok Variyani',
  readTime: '15 min read'
},
{
  id: 'dubai',
  title: 'Study in Dubai: Modern Education Hub in the Heart of UAE',
  excerpt: 'Dubai has transformed into one of the world\'s most attractive study destinations, offering international students a unique blend of world-class education, cultural diversity, and unparalleled career opportunities.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f93ccaa6-1772355210942.png",
  alt: 'Dubai Marina skyline with modern skyscrapers and waterfront',
  category: 'Dubai',
  date: '2026-01-19',
  author: 'Ashok Variyani',
  readTime: '12 min read'
},
{
  id: 'france',
  title: 'Study in France: Excellence in Education and Culture',
  excerpt: 'France has been a beacon of education and culture for centuries, attracting over 370,000 international students annually with its prestigious universities, affordable education, and rich cultural heritage.',
  image: "https://images.unsplash.com/photo-1534623982853-58a8d628e223",
  alt: 'Eiffel Tower and Paris cityscape with Seine River',
  category: 'France',
  date: '2026-01-19',
  author: 'Ashok Variyani',
  readTime: '13 min read'
},
{
  id: 'germany',
  title: 'Study in Germany: Tuition-Free World-Class Education',
  excerpt: 'Germany has become one of the most sought-after study destinations globally, attracting over 400,000 international students annually with tuition-free education at public universities and excellent career prospects.',
  image: "https://images.unsplash.com/photo-1718622065919-ab9c93218e67",
  alt: 'Brandenburg Gate in Berlin with historic German architecture',
  category: 'Germany',
  date: '2026-01-19',
  author: 'Ashok Variyani',
  readTime: '14 min read'
},
{
  id: 'japan',
  title: 'Study in Japan: Innovation, Tradition, and Academic Excellence',
  excerpt: 'Japan has emerged as a premier destination for international students seeking world-class education in technology, engineering, and sciences, offering a unique blend of ancient cultural heritage and modern innovation.',
  image: "https://images.unsplash.com/photo-1681317473971-06d8fae2264f",
  alt: 'Mount Fuji with cherry blossoms and traditional Japanese temple',
  category: 'Japan',
  date: '2026-01-19',
  author: 'Ashok Variyani',
  readTime: '13 min read'
},
{
  id: 'philippines',
  title: 'Study in Philippines: Affordable English-Medium Education in Asia',
  excerpt: 'The Philippines has become an increasingly popular destination for international students seeking affordable, English-medium education in a culturally rich and welcoming environment.',
  image: "https://images.unsplash.com/photo-1594485770508-f629fb05e932",
  alt: 'Chocolate Hills in Bohol Philippines with tropical landscape',
  category: 'Philippines',
  date: '2026-01-19',
  author: 'Ashok Variyani',
  readTime: '12 min read'
},
{
  id: 'russia',
  title: 'Study in Russia: Affordable Quality Education in Europe',
  excerpt: 'Russia has a long-standing tradition of academic excellence, attracting over 300,000 international students annually with strong programs in engineering, medicine, and sciences at highly affordable costs.',
  image: "https://images.unsplash.com/photo-1609910680412-3a49240ca5e3",
  alt: 'Saint Basil Cathedral and Red Square in Moscow Russia',
  category: 'Russia',
  date: '2026-01-19',
  author: 'Ashok Variyani',
  readTime: '12 min read'
},
{
  id: 'singapore',
  title: 'Study in Singapore: Asia\'s Premier Education Hub',
  excerpt: 'Singapore has established itself as Asia\'s leading education hub, attracting over 100,000 international students annually with world-class universities, safe environment, and excellent career opportunities.',
  image: "https://images.unsplash.com/photo-1574510169444-587f8830ba80",
  alt: 'Marina Bay Sands and Singapore skyline with Gardens by the Bay',
  category: 'Singapore',
  date: '2026-01-19',
  author: 'Ashok Variyani',
  readTime: '13 min read'
},
{
  id: 'uae',
  title: 'Study in UAE: Modern Education in the Heart of the Middle East',
  excerpt: 'The United Arab Emirates has rapidly transformed into a leading education destination, attracting over 100,000 international students with world-class branch campuses and excellent career opportunities.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_17f07d402-1774854237781.png",
  alt: 'Sheikh Zayed Grand Mosque in Abu Dhabi UAE',
  category: 'UAE',
  date: '2026-01-19',
  author: 'Ashok Variyani',
  readTime: '13 min read'
},
{
  id: 'vietnam',
  title: 'Study in Vietnam: Emerging Education Destination in Southeast Asia',
  excerpt: 'Vietnam has emerged as an exciting study destination in Southeast Asia, attracting growing numbers of international students with affordable education, rich culture, and rapid economic growth.',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d6c6b838-1772253351479.png",
  alt: 'Ha Long Bay Vietnam with limestone karsts and traditional boats',
  category: 'Vietnam',
  date: '2026-01-19',
  author: 'Ashok Variyani',
  readTime: '13 min read'
},
{
  id: 'india-france-academic-partnerships-2026',
  title: 'Study in France from India: How India–France Academic Partnerships Are Creating Global Career Pathways in 2026',
  excerpt: 'France is no longer a niche option for Indian students. Discover how India–France academic partnerships, scholarships, English-taught programmes, and the Young Professionals Scheme are creating real global career pathways in 2026.',
  image: "https://images.unsplash.com/photo-1534623982853-58a8d628e223",
  alt: 'Paris cityscape with Eiffel Tower and Seine River representing India-France academic partnership opportunities',
  category: 'France',
  date: '2026-03-05',
  author: 'Ashok Variyani',
  readTime: '14 min read'
}];





const categories = ['All', 'China', 'Dubai', 'France', 'Germany', 'Japan', 'MBBS Abroad', 'Philippines', 'Russia', 'Singapore', 'UAE', 'Vietnam'];

function BlogContent() {
  const searchParams = useSearchParams();
  const categoryFromUrl = searchParams.get('category');

  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  // Update selected category when URL parameter changes
  useEffect(() => {
    if (categoryFromUrl) {
      setSelectedCategory(categoryFromUrl);
    }
  }, [categoryFromUrl]);

  const filteredPosts = blogPosts.filter((post) => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const breadcrumbSchema: WithContext<BreadcrumbList> = generateBreadcrumbSchema([
  { name: 'Home', path: '/' },
  { name: 'Blog', path: '/blog' }]
  );

  return (
    <div className="min-h-screen bg-background">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Study Abroad Blog
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Expert insights, guides, and tips for your international education journey
            </p>
          </div>

          <div className="mb-8">
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" />

          </div>

          <div className="flex flex-wrap gap-2 mb-12 justify-center">
            {categories.map((category) =>
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-smooth ${
              selectedCategory === category ?
              'bg-primary text-primary-foreground shadow-md' :
              'bg-card text-foreground border border-border hover:border-primary hover:text-primary'}`
              }>

              {category}
            </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) =>
            <Link
              key={post.id}
              href={`/blog/${post.id}`}
              className="group bg-card border border-border rounded-lg overflow-hidden hover:shadow-elevated transition-all duration-smooth transform hover:-translate-y-1">

                <div className="relative h-48 overflow-hidden bg-muted">
                  <Image
                  src={post.image}
                  alt={post.alt}
                  fill
                  className="object-cover group-hover:scale-105 transition-transform duration-smooth"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" />


                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full">
                      {post.category}
                    </span>
                    <span className="text-xs text-muted-foreground">{post.readTime}</span>
                  </div>
                  <h2 className="text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                    {post.title}
                  </h2>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{post.author}</span>
                    <span>{post.date}</span>
                  </div>
                </div>
              </Link>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>);

}

export default function BlogPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <BlogContent />
    </Suspense>);

}